import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { GEO_OPTIONS } from '../constants/trendGeoOptions';

const VALID_GEO = new Set(GEO_OPTIONS.map((o) => o.code));

const BrandVoiceContext = createContext();

const STORAGE_BRAND = 'brandVoiceSettings';
const STORAGE_GEO = 'trendGeo';
const STORAGE_BLOG_LANG = 'blogOutputLanguage';

function normalizeBlogLanguage(value) {
  return value === 'en' ? 'en' : 'nl';
}

export const useBrandVoice = () => {
  const context = useContext(BrandVoiceContext);
  if (!context) {
    throw new Error('useBrandVoice must be used within a BrandVoiceProvider');
  }
  return context;
};

export const BrandVoiceProvider = ({ children }) => {
  const [brandVoice, setBrandVoice] = useState({
    company_name: '',
    company_sector: '',
    company_website: '',
    company_description: '',
    company_tov: ''
  });

  const [geo, setGeoState] = useState('NL');
  const [blogOutputLanguage, setBlogOutputLanguageState] = useState('nl');

  // Load from sessionStorage on mount
  useEffect(() => {
    const savedBrandVoice = sessionStorage.getItem(STORAGE_BRAND);
    if (savedBrandVoice) {
      try {
        setBrandVoice(JSON.parse(savedBrandVoice));
      } catch (error) {
        console.error('Error parsing brand voice settings:', error);
      }
    }

    const savedGeo = sessionStorage.getItem(STORAGE_GEO);
    if (savedGeo && typeof savedGeo === 'string') {
      const upper = savedGeo.toUpperCase();
      if (VALID_GEO.has(upper)) {
        setGeoState(upper);
      }
    }

    const savedLang = sessionStorage.getItem(STORAGE_BLOG_LANG);
    if (savedLang) {
      setBlogOutputLanguageState(normalizeBlogLanguage(savedLang));
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_BRAND, JSON.stringify(brandVoice));
  }, [brandVoice]);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_GEO, geo);
  }, [geo]);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_BLOG_LANG, blogOutputLanguage);
  }, [blogOutputLanguage]);

  const setGeo = useCallback((code) => {
    if (typeof code !== 'string') {
      setGeoState('NL');
      return;
    }
    const upper = code.toUpperCase();
    setGeoState(VALID_GEO.has(upper) ? upper : 'NL');
  }, []);

  const setBlogOutputLanguage = useCallback((lang) => {
    setBlogOutputLanguageState(normalizeBlogLanguage(lang));
  }, []);

  const updateBrandVoice = (field, value) => {
    setBrandVoice(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearBrandVoice = () => {
    setBrandVoice({
      company_name: '',
      company_sector: '',
      company_website: '',
      company_description: '',
      company_tov: ''
    });
  };

  const isBrandVoiceActive = () => {
    return Object.values(brandVoice).some(value => value.trim() !== '');
  };

  const getBrandVoiceForAPI = useCallback(() => {
    const apiData = {};
    Object.entries(brandVoice).forEach(([key, value]) => {
      if (value.trim() !== '') {
        apiData[key] = value;
      }
    });
    return apiData;
  }, [brandVoice]);

  const getBlogGenerationPayload = useCallback(() => ({
    ...getBrandVoiceForAPI(),
    blog_language: blogOutputLanguage
  }), [getBrandVoiceForAPI, blogOutputLanguage]);

  const isSettingsPanelActive = useMemo(() => {
    const voiceActive = Object.values(brandVoice).some(value => value.trim() !== '');
    return geo !== 'NL' || voiceActive || blogOutputLanguage !== 'nl';
  }, [geo, brandVoice, blogOutputLanguage]);

  const value = {
    brandVoice,
    updateBrandVoice,
    clearBrandVoice,
    isBrandVoiceActive,
    getBrandVoiceForAPI,
    geo,
    setGeo,
    blogOutputLanguage,
    setBlogOutputLanguage,
    getBlogGenerationPayload,
    isSettingsPanelActive
  };

  return (
    <BrandVoiceContext.Provider value={value}>
      {children}
    </BrandVoiceContext.Provider>
  );
};
